"use client";

import { useLayoutEffect, useRef, useState } from "react";
import { cn } from "../../utils/cn";

const PERCENTAGE_SCALE = 100;
const HEX_SHORTHAND_LENGTH = 3;
const HEX_RADIX = 16;
const HEX_COLOR_PATTERN = /^([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
const RGB_CHANNEL_MAX = 255;
const SRGB_LINEAR_THRESHOLD = 0.039_28;
const SRGB_LINEAR_DIVISOR = 12.92;
const SRGB_GAMMA_OFFSET = 0.055;
const SRGB_GAMMA_DIVISOR = 1.055;
const SRGB_GAMMA_EXPONENT = 2.4;
const LUMINANCE_RED_WEIGHT = 0.2126;
const LUMINANCE_GREEN_WEIGHT = 0.7152;
const LUMINANCE_BLUE_WEIGHT = 0.0722;
const CONTRAST_OFFSET = 0.05;
/** Below this contrast against a white page the swatch reads as empty space. */
const MIN_CONTRAST_AGAINST_WHITE = 1.25;
const GRADIENT_ANGLE = "135deg";
const MASK_VISIBLE = "#000";
const MASK_HIDDEN = "transparent";
const SWATCHES_VISIBLE_LIMIT = 5;
const SWATCH_BUTTON_WIDTH = 36;
const SWATCH_BUTTON_SPACING = 8;

export interface ColorSwatchProps {
  appearance?: "b2b";
  colors: string[];
  disabled?: boolean;
  isFallbackColor?: boolean;
  selected?: boolean;
  onClick?: () => void;
  className?: string;
  label: string;
}

export interface ColorSwatchGroupProps {
  appearance?: "b2b";
  swatches: Array<Omit<ColorSwatchProps, "selected"> & { id: string }>;
  selectedId?: string;
  onSelect?: (id: string) => void;
  className?: string;
}

export interface CollapsedSwatchVisibleLimitOptions {
  containerWidth?: number;
  maxVisibleLimit?: number;
  swatchCount: number;
}

function getSwatchRowWidth(itemCount: number): number {
  if (itemCount === 0) {
    return 0;
  }

  return (
    itemCount * SWATCH_BUTTON_WIDTH + (itemCount - 1) * SWATCH_BUTTON_SPACING
  );
}

export function getCollapsedSwatchVisibleLimit({
  containerWidth,
  maxVisibleLimit = SWATCHES_VISIBLE_LIMIT,
  swatchCount,
}: CollapsedSwatchVisibleLimitOptions): number {
  if (swatchCount === 0) {
    return 0;
  }

  const maxCollapsedSwatches = Math.max(
    0,
    Math.min(swatchCount, maxVisibleLimit)
  );

  if (!(containerWidth && containerWidth > 0)) {
    return maxCollapsedSwatches;
  }

  for (
    let visibleCount = maxCollapsedSwatches;
    visibleCount >= 0;
    visibleCount--
  ) {
    const hidesSwatches = visibleCount < swatchCount;
    const itemCount = visibleCount + (hidesSwatches ? 1 : 0);

    if (getSwatchRowWidth(itemCount) <= containerWidth) {
      return visibleCount;
    }
  }

  return 0;
}

function parseHexColor(color: string): [number, number, number] | null {
  const hex = color.trim().replace(/^#/, "");
  const normalized =
    hex.length === HEX_SHORTHAND_LENGTH
      ? hex
          .split("")
          .map((char) => char + char)
          .join("")
      : hex;

  const match = HEX_COLOR_PATTERN.exec(normalized);

  if (!match) {
    return null;
  }

  const [, red, green, blue] = match;

  return [
    Number.parseInt(red, HEX_RADIX),
    Number.parseInt(green, HEX_RADIX),
    Number.parseInt(blue, HEX_RADIX),
  ];
}

function toLinearChannel(channel: number): number {
  const value = channel / RGB_CHANNEL_MAX;

  return value <= SRGB_LINEAR_THRESHOLD
    ? value / SRGB_LINEAR_DIVISOR
    : ((value + SRGB_GAMMA_OFFSET) / SRGB_GAMMA_DIVISOR) ** SRGB_GAMMA_EXPONENT;
}

function getRelativeLuminance([red, green, blue]: [
  number,
  number,
  number,
]): number {
  return (
    LUMINANCE_RED_WEIGHT * toLinearChannel(red) +
    LUMINANCE_GREEN_WEIGHT * toLinearChannel(green) +
    LUMINANCE_BLUE_WEIGHT * toLinearChannel(blue)
  );
}

/** A near-white color blends into the page, so it needs an outline of its own. */
function isNearWhite(color: string): boolean {
  const rgb = parseHexColor(color);

  if (!rgb) {
    return false;
  }

  const contrastAgainstWhite =
    (1 + CONTRAST_OFFSET) / (getRelativeLuminance(rgb) + CONTRAST_OFFSET);

  return contrastAgainstWhite < MIN_CONTRAST_AGAINST_WHITE;
}

/**
 * Swatches that are (partly) near-white blend into the page, so they need an
 * outline to still read as a color swatch.
 */
export function hasNearWhiteColor(colors: string[]): boolean {
  return colors.some(isNearWhite);
}

/** Hard stops that split the swatch into one equal band per color. */
function buildBandedGradient(bandColors: string[]): string {
  const stepSize = PERCENTAGE_SCALE / bandColors.length;
  const stops = bandColors
    .map((color, index) => {
      const start = index * stepSize;
      const end = (index + 1) * stepSize;
      return `${color} ${start}%, ${color} ${end}%`;
    })
    .join(", ");

  return `linear-gradient(${GRADIENT_ANGLE}, ${stops})`;
}

function buildGradient(colors: string[]): string {
  if (colors.length === 0) {
    return "transparent";
  }
  if (colors.length === 1) {
    return colors[0];
  }

  return buildBandedGradient(colors);
}

/**
 * Mask that reveals the outline only over the near-white bands, so a white half
 * gets an edge while the rest of the swatch stays borderless. Returns undefined
 * when every band is near-white — the unmasked border already covers that.
 */
export function buildNearWhiteOutlineMask(
  colors: string[]
): string | undefined {
  const nearWhiteBands = colors.map(isNearWhite);

  if (nearWhiteBands.every(Boolean)) {
    return;
  }

  return buildBandedGradient(
    nearWhiteBands.map((nearWhite) => (nearWhite ? MASK_VISIBLE : MASK_HIDDEN))
  );
}

export function ColorSwatch(props: Readonly<ColorSwatchProps>) {
  const background = buildGradient(props.colors);
  const needsOutline =
    hasNearWhiteColor(props.colors) &&
    !(props.appearance === "b2b" && props.selected);
  const selectedRingColor =
    props.appearance === "b2b" ? "ring-zinc-900" : "ring-alabastergrey";
  const outlineMask = needsOutline
    ? buildNearWhiteOutlineMask(props.colors)
    : undefined;

  return (
    <button
      aria-label={props.label}
      aria-pressed={props.selected}
      className={cn(
        "mr-1 h-6 w-9 shrink-0 rounded border-1 border-transparent transition-all last:mr-0",
        props.appearance === "b2b" && "appearance-none bg-white",
        props.selected
          ? cn("ring-1 ring-offset-2", selectedRingColor)
          : "hover:ring-1 hover:ring-alabastergrey hover:ring-offset-2",
        props.disabled && "cursor-not-allowed opacity-50",
        props.className
      )}
      disabled={props.disabled}
      onClick={props.onClick}
      title={props.label}
      type="button"
    >
      <span
        className="relative block h-full w-full rounded-sm"
        data-fallback-color={props.isFallbackColor}
        style={{ background }}
      >
        {needsOutline && (
          <span
            aria-hidden="true"
            className={cn(
              "absolute inset-0 rounded-sm border",
              props.appearance === "b2b" ? "border-border" : "border-jetblack"
            )}
            data-testid="color-swatch-outline"
            style={
              outlineMask
                ? { maskImage: outlineMask, WebkitMaskImage: outlineMask }
                : undefined
            }
          />
        )}
      </span>
    </button>
  );
}

export function ColorSwatchGroup(props: Readonly<ColorSwatchGroupProps>) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number>();
  const fieldsetRef = useRef<HTMLFieldSetElement>(null);

  useLayoutEffect(() => {
    const fieldsetElement = fieldsetRef.current;
    if (!fieldsetElement) {
      return;
    }

    const updateContainerWidth = (width: number) => {
      const nextWidth = Math.floor(width);
      setContainerWidth((previousWidth) =>
        previousWidth === nextWidth ? previousWidth : nextWidth
      );
    };

    updateContainerWidth(fieldsetElement.getBoundingClientRect().width);

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      updateContainerWidth(
        entries[0]?.contentRect.width ??
          fieldsetElement.getBoundingClientRect().width
      );
    });

    observer.observe(fieldsetElement);

    return () => observer.disconnect();
  }, []);

  const collapsedVisibleLimit = getCollapsedSwatchVisibleLimit({
    containerWidth,
    swatchCount: props.swatches.length,
  });
  const hasMore = !isExpanded && collapsedVisibleLimit < props.swatches.length;
  const visibleSwatches = hasMore
    ? props.swatches.slice(0, collapsedVisibleLimit)
    : props.swatches;
  const hiddenCount = props.swatches.length - visibleSwatches.length;

  return (
    <fieldset
      className={cn(
        "flex w-full min-w-0 max-w-full items-center gap-1 py-1 [min-inline-size:0]",
        isExpanded ? "flex-wrap" : "flex-nowrap",
        isExpanded && props.appearance === "b2b" && "gap-y-3",
        props.className
      )}
      ref={fieldsetRef}
    >
      {visibleSwatches.map((swatch) => (
        <ColorSwatch
          appearance={props.appearance}
          colors={swatch.colors}
          isFallbackColor={swatch.isFallbackColor}
          key={swatch.id}
          label={swatch.label}
          onClick={() => props.onSelect?.(swatch.id)}
          selected={props.selectedId === swatch.id}
        />
      ))}

      {hasMore && !isExpanded && (
        <button
          aria-label={`${hiddenCount} more colors`}
          className={cn(
            "mr-1 h-5 w-9 shrink-0 rounded border-1 border-transparent transition-all last:mr-0",
            "p-0 font-medium text-gray-600 text-xs ring-1 ring-alabastergrey ring-offset-1",
            "hover:h-6 hover:w-9 hover:bg-whitesmoke hover:ring-offset-2"
          )}
          onClick={() => setIsExpanded(true)}
          type="button"
        >
          +{hiddenCount}
        </button>
      )}
    </fieldset>
  );
}
